import { CurrencyPipe } from '@angular/common';
import { Directive, ElementRef, HostListener, Input, OnInit, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';

const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => CurrencyFormatDirective),
    multi: true,
};

@Directive({
    selector: '[currencyFormat]',
    standalone: true,
    providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR, CurrencyPipe, ReactiveFormsModule, FormsModule],
})
export class CurrencyFormatDirective implements ControlValueAccessor, OnInit {
    @Input()
    set maxDigits(maxDigits: number) {
        this.setRegex(maxDigits);
    }
    private readonly element: HTMLInputElement;
    private digitRegex: RegExp;
    private lastValid = '';

    constructor(
        private readonly elementRef: ElementRef,
        private readonly currencyPipe: CurrencyPipe,
    ) {
        this.element = this.elementRef.nativeElement;
        this.setRegex();
    }

    private setRegex(maxDigits?: number) {
        this.digitRegex = new RegExp(this.regexString(maxDigits), 'g');
    }

    // build the regex based on max pre decimal digits allowed
    private regexString(maxDigits?: number) {
        const maxStr = maxDigits ? `{0,${maxDigits}}` : `+`;
        return `^(\\d${maxStr}(\\.\\d{0,2})?|\\.\\d{0,2})$`;
    }

    onChange: any = () => {};
    onTouched: any = () => {};

    ngOnInit() {
        this.element.value = this.currencyPipe.transform(this.element.value, 'USD');
    }

    @HostListener('input', ['$event.target.value'])
    onInput(value: string): void {
        // Format the value as US currency
        // const formattedValue = this.formatAsCurrency(value);

        const formattedValue = (value.match(this.digitRegex) || []).join('');
        if (formattedValue || !value) {
            this.lastValid = formattedValue;
        }

        // Update the input value and the associated control
        this.element.value = formattedValue || this.lastValid;
        this.onChange(this.parseCurrency(this.element.value));
    }

    @HostListener('focus', ['$event.target.value'])
    onFocus(value: string) {
        // on focus remove currency formatting
        if (value) {
            this.element.value = this.parseCurrency(value).toString();
        }
    }

    @HostListener('blur', ['$event.target.value'])
    onBlur(value: string | number) {
        // on blur, add currency formatting
        this.onTouched();
        if (value) {
            this.element.value = this.formatAsCurrency(value.toString());
        }
    }

    // Format the value as US currency
    private formatAsCurrency(value: string): string {
        // Implement your currency formatting logic here
        // Example: You might want to use a library like Intl.NumberFormat
        // or create a custom formatting function
        return this.currencyPipe.transform(value, 'USD');
    }

    // Parse the currency value to a numeric value
    private parseCurrency(value: string): number | undefined {
        // Implement your logic to parse the currency value to a number
        // Example: Remove non-numeric characters and convert to a number
        if (value) {
            return parseFloat(value.replace(/[^0-9.]/g, ''));
        }
        return undefined;
    }

    writeValue(value: any): void {
        // Write initial value to the view
        this.element.value = this.formatAsCurrency(value);
    }

    registerOnChange(fn: any): void {
        // Register callback function to update value
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        // Register callback function on touch
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        // Implement if you want to handle the disabled state
        this.element.disabled = isDisabled;
    }
}
